import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import cloudflare from "../../assets/trusted-partners/cloudflare.png";
import ewsHosting from "../../assets/trusted-partners/ews-hosting.png";
import gcash from "../../assets/trusted-partners/gcash.png";
// import hitpay from "../../assets/trusted-partners/hitpay.png";
import maya from "../../assets/trusted-partners/maya.png";
import paypal from "../../assets/trusted-partners/paypal.webp";
import qrPh from "../../assets/trusted-partners/qr-ph.png";
import trustedd from "../../assets/trustedd.webp";
import dpoDps from "../../assets/dpo-trusted-app.webp";

const TrustedPartners = () => {
  const SPACING = 280;
  const SPEED = 1.2;
  const WAVE_AMPLITUDE = 70;
  const WAVE_STRETCH = 350;

  const partnerLogos = [
    cloudflare,
    ewsHosting,
    gcash,
    // hitpay,
    maya,
    paypal,
    qrPh,
    trustedd,
    dpoDps,
  ];
  const partnerNames = [
    "Cloudflare",
    "EWS Hosting",
    "GCash",
    // "HitPay",
    "Maya",
    "PayPal",
    "QR Ph",
    "Trusted Partner",
    "DPO/DPS Registered",
  ];
  const partnerColors = [
    "#F38020",
    "#2563EB",
    "#007DFF",
    "#FF6B00",
    "#00D632",
    "#003087",
    "#1E40AF",
    "#0D9488",
    "#2D3E8F",
  ];

  const items = partnerLogos.map((logo, i) => {
    const isTrustedLogo = logo === trustedd;
    const isDpoDpsLogo = logo === dpoDps;
    return {
      id: i,
      color: partnerColors[i % partnerColors.length],
      logo: logo,
      name: partnerNames[i],
      padding: isTrustedLogo ? "p-3" : isDpoDpsLogo ? "p-4" : "p-8",
    };
  });

  const containerRef = useRef(null);
  const itemsRef = useRef([]);
  const progressRef = useRef(0);
  const requestRef = useRef(null);
  const isDraggingRef = useRef(false);
  const lastPointerXRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const totalWidth = items.length * SPACING;

  const updatePositions = () => {
    if (!containerRef.current) return;
    const centerX = containerRef.current.clientWidth / 2;
    items.forEach((item, index) => {
      const el = itemsRef.current[index];
      if (!el) return;
      let x = (index * SPACING + progressRef.current) % totalWidth;
      if (x < -SPACING) x += totalWidth;
      if (x > totalWidth - SPACING) x -= totalWidth;
      const visualX = x;
      const distFromCenter = visualX + SPACING / 2 - centerX;
      const phase = distFromCenter / WAVE_STRETCH;
      const yOffset = -WAVE_AMPLITUDE * Math.sin(phase);
      el.style.transform = `translate3d(${visualX}px, ${yOffset}px, 0)`;
    });
  };

  const animate = () => {
    if (!containerRef.current) return;
    if (!isDraggingRef.current) {
      progressRef.current -= SPEED;
    }
    if (Math.abs(progressRef.current) >= totalWidth) progressRef.current = 0;
    updatePositions();
    requestRef.current = requestAnimationFrame(animate);
  };

  const handlePrev = () => {
    progressRef.current += SPACING;
    updatePositions();
  };

  const handleNext = () => {
    progressRef.current -= SPACING;
    updatePositions();
  };

  const handlePointerDown = (event) => {
    isDraggingRef.current = true;
    setIsDragging(true);
    lastPointerXRef.current = event.clientX;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!isDraggingRef.current) return;
    const deltaX = event.clientX - lastPointerXRef.current;
    lastPointerXRef.current = event.clientX;
    progressRef.current += deltaX;
    updatePositions();
  };

  const stopDragging = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);
  };

  useEffect(() => {
    updatePositions();
    requestRef.current = requestAnimationFrame(animate);
    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, []);

  return (
    <section className="relative z-10 overflow-hidden border-t border-[#93c5fd]/30 bg-transparent px-6 py-24 backdrop-blur-3xl md:px-12 md:py-32">
      <div className="mx-auto mb-16 max-w-6xl px-4 text-center">
        <span className="mb-6 inline-block rounded-full border border-[#93c5fd]/40 bg-white/70 px-5 py-2 text-xs font-bold tracking-widest text-[#5A7FA8] uppercase shadow-sm backdrop-blur-md">
          Powered By
        </span>
        <h2 className="text-5xl font-bold tracking-tight text-[#1a3a5c] md:text-6xl lg:text-7xl">
          Trusted Apps
        </h2>
        <p className="mt-4 text-sm font-medium text-[#5A7FA8]">
          Drag left or right to explore partner apps
        </p>
      </div>

      <div className="w-full max-w-none mask-[linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
        <div
          ref={containerRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={stopDragging}
          onPointerCancel={stopDragging}
          onPointerLeave={stopDragging}
          className={`relative h-80 w-full overflow-hidden touch-pan-y select-none sm:h-96 md:h-112 lg:h-128 ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}>
          <div className="pointer-events-none absolute top-4 left-1/2 z-20 -translate-x-1/2 rounded-full border border-sky-200/60 bg-white/85 px-4 py-1.5 text-xs font-semibold tracking-wide text-[#4A6D92] shadow-sm">
            Hand-draggable
          </div>

          <button
            type="button"
            onClick={handlePrev}
            aria-label="Show previous partner"
            className="absolute top-1/2 left-3 z-30 -translate-y-1/2 rounded-full border border-white/70 bg-white/90 p-2.5 text-[#1a3a5c] shadow-lg backdrop-blur transition hover:scale-105 hover:bg-white md:hidden">
            <ChevronLeft className="h-5 w-5" />
          </button>

          <button
            type="button"
            onClick={handleNext}
            aria-label="Show next partner"
            className="absolute top-1/2 right-3 z-30 -translate-y-1/2 rounded-full border border-white/70 bg-white/90 p-2.5 text-[#1a3a5c] shadow-lg backdrop-blur transition hover:scale-105 hover:bg-white md:hidden">
            <ChevronRight className="h-5 w-5" />
          </button>

          {items.map((item, index) => (
            <div
              key={index}
              ref={(el) => (itemsRef.current[index] = el)}
              className="absolute top-1/2 left-0 -mt-16 flex w-max items-center justify-center will-change-transform sm:-mt-18 md:-mt-22 lg:-mt-26"
              style={{ transform: "translate3d(-1000px, 0, 0)" }}>
              <div
                className={`flex h-28 w-40 cursor-pointer items-center justify-center overflow-hidden rounded-[2rem] border border-white/10 bg-white shadow-2xl backdrop-blur-3xl transition-all duration-300 hover:-translate-y-2 hover:scale-105 hover:border-white/30 hover:bg-white-800/80 hover:shadow-[0_20px_40px_rgba(0,0,0,0.5)] sm:h-32 sm:w-48 md:h-40 md:w-56 lg:h-44 lg:w-64 ${item.padding}`}
                style={{ borderBottomColor: item.color, borderBottomWidth: "4px" }}>
                {item.logo ? (
                  <img
                    src={item.logo}
                    alt={item.name}
                    className="h-full w-full object-contain filter transition-all duration-300"
                  />
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrustedPartners;
